import { useEffect, useRef } from 'react';
import { useAuth } from '../store/auth.js';
import { getInitData, getTelegramFirstName } from '../lib/telegram.js';
import { postAuthVerify, AuthVerifyError } from '../lib/api-client.js';

const RETRY_DELAYS_MS = [1000, 2000, 4000];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function useTelegramAuth(): void {
  const jwt = useAuth((s) => s.jwt);
  const setAuth = useAuth((s) => s.setAuth);
  const setAuthenticating = useAuth((s) => s.setAuthenticating);
  const setError = useAuth((s) => s.setError);
  const setPendingDeletion = useAuth((s) => s.setPendingDeletion);
  // Guard against double-invocation (React StrictMode) AND avoid putting
  // `status` in the dep array: setAuthenticating() flips status, which would
  // re-run the effect and trigger cleanup (cancelled=true) BEFORE the
  // in-flight postAuthVerify resolved — so setAuth was never called and the
  // Mini App stayed on the Splash "Connecting…" screen forever.
  const startedRef = useRef(false);

  useEffect(() => {
    if (jwt || startedRef.current) return;
    const initData = getInitData();
    if (!initData) {
      setError('telegram_init_data_missing');
      return;
    }
    startedRef.current = true;
    setAuthenticating();

    void (async (): Promise<void> => {
      const attempts = RETRY_DELAYS_MS.length + 1;
      for (let i = 0; i < attempts; i++) {
        try {
          const res = await postAuthVerify(initData);
          // T011 — overlay Telegram first_name from initDataUnsafe so the
          // store has it synchronously without waiting for /api/v1/me.
          // Server-returned firstName (if present) wins; otherwise use TG.
          const tgFirstName = getTelegramFirstName();
          const userWithName = {
            ...res.user,
            firstName: res.user.firstName ?? tgFirstName,
          };
          setAuth(res.jwt, userWithName);
          return;
        } catch (err) {
          // Don't retry on 4xx (bad initData, malformed body) — only transient (network / 5xx)
          if (err instanceof AuthVerifyError && err.status >= 400 && err.status < 500) {
            if (err.pendingDeletion) {
              setPendingDeletion(err.pendingDeletion);
              return;
            }
            setError(err.message);
            return;
          }
          if (i < attempts - 1) {
            await sleep(RETRY_DELAYS_MS[i]);
            continue;
          }
          setError(err instanceof Error ? err.message : 'auth_failed');
        }
      }
    })();
  }, [jwt, setAuth, setAuthenticating, setError, setPendingDeletion]);
}
